import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { stringify } from 'qs';
import { Observable, Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import SwiperCore, { A11y, Navigation, SwiperOptions, Virtual } from 'swiper';

import { getActivityDetailRouteName } from '~/attendee/helpers/activity/activity-detail-route-name.helper';
import { Activity } from '~/core/model/activity.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { LoaderService } from '~/core/services/loader/loader.service';

import { environment } from '~environments/environment';
import {
  buildActivityFilterObject,
  buildParametersObject,
  DEFAULT_PARAMETERS,
} from './helpers/widget.helper';
import { WidgetParametersInterface } from './interfaces/widget-parameters.interface';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';

SwiperCore.use([Virtual, A11y, Navigation]);

const MOBILE_SLIDE_PER_PAGE = 1.4;
const MOBILE_BREAKPOINT = 480;

/**
 * V1 Query params :
 *
 * latitude: float
 * longitude: float
 * distance: int: 100
 * sport: int
 * slider: boolean: false
 * perpage: int: 4
 * limit: int: 25
 * organizer: UUID (string)
 * group: int
 *
 * V2 Query params :
 *
 * lang: string
 */
@Component({
  selector: 'app-widget',
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WidgetComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  public readonly iconTypeEnum = VtmnPlayIconTypeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;

  public activityCollection: Activity[] = [];
  public config: SwiperOptions = {
    direction: 'horizontal',
    slidesPerView: MOBILE_SLIDE_PER_PAGE,
    slidesPerGroup: 1,
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    breakpoints: {
      [MOBILE_BREAKPOINT]: {
        slidesPerView: DEFAULT_PARAMETERS.perPage,
        slidesPerGroup: DEFAULT_PARAMETERS.perPage,
      },
    },
    keyboard: true,
    mousewheel: true,
    scrollbar: false,
  };

  public isLoading: boolean;
  public langs: string[];
  public showGeolocationErrorMessage = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly activityService: ActivityService,
    public readonly translate: TranslateService,
    public readonly loaderService: LoaderService,
    public readonly cdr: ChangeDetectorRef,
    private readonly localizeRouterService: LocalizeRouterService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.loaderService.loading$.subscribe(() => {
      this.isLoading = this.loaderService.isLoading();
      this.cdr.detectChanges();
    });

    this.loaderService.start();

    this.subscriptions.add(
      this.route.queryParams
        .pipe(
          map((queryParams) => {
            const wigdetParams = buildParametersObject(queryParams);

            this.init(wigdetParams);

            return wigdetParams;
          }),

          mergeMap((parameters) => this.fetchActivityCollection(parameters))
        )
        .subscribe({
          next: (activityCollection) => {
            this.activityCollection = activityCollection;
            this.loaderService.stop();
          },
          error: (error) => {
            if (error.type === 'GeolocationPositionError' && error.code === 1) {
              this.showGeolocationErrorMessage = true;
            }
            this.loaderService.stop();
          },
        })
    );
  }

  /**
   * Fetch activities from API according to widget params
   */
  private fetchActivityCollection(
    parameters: WidgetParametersInterface
  ): Observable<Activity[]> {
    const filters = buildActivityFilterObject(parameters, true);

    return this.activityService
      .getActivities(filters)
      .pipe(map((paginatedResponse) => paginatedResponse.items));
  }

  /**
   * Updates slider config, lang, etc.
   */
  private init(parameters: WidgetParametersInterface): void {
    // Updates slider config according to widget parameters.
    const config = this.config.breakpoints[MOBILE_BREAKPOINT];

    config.slidesPerView = parameters.perPage;
    config.slidesPerGroup = parameters.perPage;

    // filled available language to lang selector component
    this.langs = parameters.langs;
  }

  public activityUrl(activity: Activity): string {
    const path = getActivityDetailRouteName(activity.type, activity.sku);
    const translatedPath = this.localizeRouterService.translateRoute([path]);
    const baseUrl = environment.applications.front.url;

    return `${baseUrl}${translatedPath}?${stringify({
      utm_source: 'widget_sport_activities',
      utm_medium: 'referral',
      utm_campaign: 'widget_sport_activities',
    })}`;
  }
}
